import { Component, Prop, h, State, Watch, Listen } from '@stencil/core';

import {
	FooterLinks,
	OntarioFooterType,
	ThreeColumnOptions,
	TwoColumnOptions,
	SimpleFooterLinks,
} from './ontario-footer-interface';
import { ExpandedFooterWrapper, FooterColumn, FooterSocialLinksProps, SimpleFooter } from './components';

import { isInvalidTwoColumnOptions, isInvalidThreeColumnOptions } from './utils';
import { Language } from '../../utils/common/language-types';
import { validateLanguage } from '../../utils/validation/validation-functions';
import { ConsoleMessageClass } from '../../utils/console-message/console-message';
import { ConsoleType } from '../../utils/console-message/console-message.enum';
import { getImageAssetSrcPath } from '../../utils/helper/assets';

import translations from '../../translations/global.i18n.json';
import { HeaderLanguageToggleEventDetails } from '../../utils/events/common-events.interface';

/**
 * Ontario Footer renders simple or expanded footer patterns for Ontario sites and applications.
 *
 * For component guidance, see:
 * - https://designsystem.ontario.ca/components/detail/simple-footer.html
 * - https://designsystem.ontario.ca/components/detail/expanded-footer.html
 * - https://designsystem.ontario.ca/developer-docs/components/ontario-footer/
 */
@Component({
	tag: 'ontario-footer',
	styleUrl: 'ontario-footer.scss',
	shadow: true,
	assetsDirs: ['assets'],
})
export class OntarioFooter {
	/**
	 * The language of the component.
	 * This is used for translations, and is by default set through event listeners checking for a language property from the header. If no language is passed, it will default to English.
	 */
	@Prop({ mutable: true }) language: Language;

	/**
	 * The type of footer to be rendered. If no prop is provided, it will default to the 'default' type.
	 */
	@Prop() type: OntarioFooterType = 'default';

	/**
	 * A prop that stores the required links for all footers.
	 * Available options are 'accessibilityLink', 'privacyLink', 'contactLink','termsOfUseLink' and 'printerLink'
	 */
	@Prop() footerLinks: FooterLinks | string;

	/**
	 * Social media links to render in the footer.
	 * Available options are 'facebook', 'twitter', 'instagram' and 'youtube'
	 */
	@Prop() socialLinks: FooterSocialLinksProps | string;

	/**
	 * Stores the titles and content for the expanded two column footer.
	 */
	@Prop() twoColumnOptions?: TwoColumnOptions | string;

	/**
	 * Stores the titles and content for the expanded three column footer.
	 */
	@Prop() threeColumnOptions?: ThreeColumnOptions | string;

	/**
	 * Top margin for the footer. By default, this prop is set to `true`, which adds a margin top value of `5rem`.
	 * If set to `false`, the top margin value will be set to zero.
	 */
	@Prop() topMargin: boolean = true;

	/**
	 * The base path to an assets folder containing the Design System assets
	 */
	@Prop() assetBasePath: string;

	@State() translations: any = translations;

	@State() private footerLinksState: FooterLinks;

	@State() private socialLinksState: FooterSocialLinksProps;

	@State() private twoColumnState: TwoColumnOptions;

	@State() private threeColumnState: ThreeColumnOptions;

	/**
	 * This listens for the `setAppLanguage` event sent from the test language toggler when it is
	 * connected to the DOM. It is used for the initial language when the input component loads.
	 */
	@Listen('setAppLanguage', { target: 'window' })
	handleSetAppLanguage(event: CustomEvent<Language>) {
		if (!this.language) {
			this.language = validateLanguage(event);
		}
	}

	/**
	 * Handles an update to the language should the user request a language update from the language toggle.
	 * @param {CustomEvent} - The language that has been selected.
	 */
	@Listen('headerLanguageToggled', { target: 'window' })
	handleHeaderLanguageToggled(event: CustomEvent<HeaderLanguageToggleEventDetails>) {
		this.language = validateLanguage(event.detail.newLanguage);
	}

	@Watch('footerLinks')
	private processFooterLinks() {
		this.parseOptions(this.footerLinks);
	}

	@Watch('socialLinks')
	private processSocialLinks() {
		this.parseOptions(this.socialLinks);
	}

	@Watch('twoColumnOptions')
	private processTwoColumnOptions() {
		this.twoColumnOptions && this.parseOptions(this.twoColumnOptions);
		this.verifyTwoColumnOptions();
	}

	@Watch('threeColumnOptions')
	private processThreeColumnOptions() {
		this.threeColumnOptions && this.parseOptions(this.threeColumnOptions);
		this.verifyThreeColumnOptions();
	}

	private isTwoColumnLayout = (): boolean => this.type === 'twoColumn';
	private isThreeColumnLayout = (): boolean => this.type === 'threeColumn';
	private isExpandedLayout = (): boolean => this.isTwoColumnLayout() || this.isThreeColumnLayout();

	private verifyTwoColumnOptions() {
		if (this.isTwoColumnLayout() && isInvalidTwoColumnOptions(this.twoColumnState)) {
			const message = new ConsoleMessageClass();
			message
				.addDesignSystemTag()
				.addMonospaceText(' twoColumnOptions ')
				.addRegularText('for')
				.addMonospaceText(' <ontario-footer> ')
				.addRegularText('were not fully set. Please review your values and ensure all options are set.')
				.printMessage();
		}
	}

	private verifyThreeColumnOptions() {
		if (this.isThreeColumnLayout() && isInvalidThreeColumnOptions(this.threeColumnState)) {
			const message = new ConsoleMessageClass();
			message
				.addDesignSystemTag()
				.addMonospaceText(' threeColumnOptions ')
				.addRegularText('for')
				.addMonospaceText(' <ontario-footer> ')
				.addRegularText('were not fully set. Please review your values and ensure all options are set.')
				.printMessage();
		}
	}

	private parseOptions(
		optionType: string | FooterLinks | FooterSocialLinksProps | TwoColumnOptions | ThreeColumnOptions,
	) {
		if (!optionType) return;

		try {
			const parsed = typeof optionType === 'string' ? JSON.parse(optionType) : optionType;

			if (optionType === this.footerLinks) this.footerLinksState = parsed;
			else if (optionType === this.socialLinks) this.socialLinksState = parsed;
			else if (optionType === this.twoColumnOptions) this.twoColumnState = parsed;
			else this.threeColumnState = parsed;
		} catch (error) {
			const message = new ConsoleMessageClass();
			message
				.addDesignSystemTag()
				.addRegularText(' failed to parse props for ')
				.addMonospaceText('<ontario-footer>')
				.addRegularText(' in ')
				.addMonospaceText('parseOptions()')
				.addRegularText(' method \n ')
				.addMonospaceText(error.stack)
				.printMessage(ConsoleType.Error);
		}
	}

	/**
	 * Generate CSS url to the background image
	 * @returns path to the background image
	 */
	private getBackgroundImagePath(): string {
		const supergraphicLogoFile = this.isExpandedLayout()
			? 'footer-expanded-supergraphic-logo.svg'
			: 'footer-default-supergraphic-logo.svg';

		return `url(${getImageAssetSrcPath(supergraphicLogoFile, this.assetBasePath)})`;
	}

	private getFooterClasses() {
		let classes = 'ontario-footer ontario-footer--default';

		if (!this.topMargin) {
			classes = `${classes}  ontario-margin-top-0-!`;
		}

		return classes;
	}

	private getFooterLinks(): SimpleFooterLinks {
		const { language, translations, footerLinksState } = this;
		const { accessibilityLink, privacyLink, contactLink, termsOfUseLink, printerLink } = footerLinksState ?? {};

		const links: SimpleFooterLinks = {
			accessibilityLink: {
				href: accessibilityLink?.href ?? translations.accessibilityLink.link[language],
				text: accessibilityLink?.text ?? translations.accessibilityLink.text[language],
			},
			privacyLink: {
				href: privacyLink?.href ?? translations.privacyLink.link[language],
				text: privacyLink?.text ?? translations.privacyLink.text[language],
			},
			printerLink: {
				href: printerLink?.href ?? translations.printerLink.link[language],
				text: printerLink?.text ?? translations.printerLink.text[language],
			},
		};

		if (contactLink) {
			links.contactLink = {
				href: contactLink.href,
				text: contactLink.text ?? translations.contactUs[language],
			};
		}

		if (termsOfUseLink) {
			links.termsOfUseLink = {
				href: termsOfUseLink.href,
				text: termsOfUseLink.text ?? translations.termsOfUse[language],
			};
		}

		return links;
	}

	componentWillLoad() {
		this.processFooterLinks();
		this.processSocialLinks();
		this.processTwoColumnOptions();
		this.processThreeColumnOptions();

		this.language = validateLanguage(this.language);
	}

	render() {
		const { socialLinksState, twoColumnState, threeColumnState, topMargin } = this;
		const footerLinks = this.getFooterLinks();

		if (this.isTwoColumnLayout()) {
			return (
				<ExpandedFooterWrapper
					footerLinks={footerLinks}
					topMargin={topMargin}
					backgroundImagePath={this.getBackgroundImagePath()}
				>
					<FooterColumn data={twoColumnState.column1} />
					<FooterColumn data={twoColumnState.column2} socialLinks={socialLinksState} />
				</ExpandedFooterWrapper>
			);
		}

		if (this.isThreeColumnLayout()) {
			return (
				<ExpandedFooterWrapper
					footerLinks={footerLinks}
					topMargin={topMargin}
					backgroundImagePath={this.getBackgroundImagePath()}
				>
					<FooterColumn data={threeColumnState.column1} isThreeColLayout isFullWidthInMediumLayout />
					<FooterColumn data={threeColumnState.column2} isThreeColLayout />
					<FooterColumn data={threeColumnState.column3} socialLinks={socialLinksState} isThreeColLayout />
				</ExpandedFooterWrapper>
			);
		}

		return (
			<footer class={this.getFooterClasses()} style={{ '--imagePath': this.getBackgroundImagePath() }}>
				<SimpleFooter {...footerLinks} />
			</footer>
		);
	}
}
